import { useEffect, useRef, useState } from "react";
import { useCasoAtual, useCelular, useEstado, useJanela } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { Fechar } from "./comuns.jsx";
import FichaDoCaso from "./FichaDoCaso.jsx";
import CabecalhoDoPainel from "./CabecalhoDoPainel.jsx";
import ReuniaoAberta from "./ReuniaoAberta.jsx";
import GravadorDeReuniao from "./GravadorDeReuniao.jsx";

/*
 * Uma coluna, uma rolagem — as abas morreram.
 *
 * Eram duas ("Caso" e "Reunião") e a segunda ficava VAZIA na maioria dos casos:
 * um botão de gravar e um vazio de 2000px. Pior que o desperdício era o que ela
 * escondia — gravação em curso só aparecia dentro dela, então quem estava na
 * ficha não via que o microfone estava ligado. É o mesmo motivo que juntou
 * "Caixa" e "Contatos": duas portas para o mesmo assunto obrigam a escolher a
 * tela antes de saber o que se quer.
 *
 * Agora a reunião é SEÇÃO do caso, a gravação viva é uma faixa presa no alto (a
 * que se vê de qualquer altura da rolagem) e abrir uma reunião gravada empurra
 * a coluna para o detalhe dela, com volta — navegação em pilha, não em aba.
 *
 * `?painel=` continua sendo o endereço: qualquer valor que não seja "0" abre a
 * coluna, e `?painel=reunioes&gravacao=12` (o link que a trilha da conversa
 * manda) abre direto na reunião 12. Com o nome de uma SEÇÃO da ficha
 * (`copiloto`, `agora`, `ficha`, `pendencias`, `reunioes`) a coluna rola até
 * ela — os valores de aba de antes (`case`, `ai`, `call`) são traduzidos em
 * `navegacao.jsx`, porque link antigo circula por mensagem.
 */

/**
 * A coluna da direita.
 *
 * Em tela larga ela empurra o conteúdo; em tela estreita, flutua por cima — é a
 * mesma coluna, e é o `empurra` que decide qual das duas ela é agora.
 *
 * Onde ela FLUTUA continua sendo um `<div>` posicionado, e não um `Sheet`: o
 * `Sheet` desenha num portal em `document.body`, fora do `display:contents` que
 * a `Casca` marca como `inert` quando a petição está na frente. A coluna sairia
 * de dentro do que a petição desliga, e o teclado voltaria a cair aqui por baixo
 * de uma superfície opaca — o defeito que o `inert` existe para não deixar
 * acontecer. O ✕ desta coluna é a saída, e a URL é quem a fecha.
 */
export default function Painel({ empurra, aoRecolher }) {
  const { caso } = useEstado();
  const largo = useJanela((j) => j.largura >= 1180);
  const { secaoDoPainel, ajustar, gravacaoApontada } = useNavegacao();
  const celular = useCelular();
  const atual = useCasoAtual();
  const rolagem = useRef(null);
  /* O fio embaixo do cabeçalho só existe quando há conteúdo por baixo dele: a
     coluna no topo é uma superfície só, e um fio ali seria decoração. */
  const [rolou, setRolou] = useState(false);
  const reuniao = gravacaoApontada || null;
  /* A hora de gravar é a que o SERVIDOR diz, pela ação do passo — a mesma lista
     fechada que a ficha usa para decidir qual seção abre. Fora dela o gravador
     não desaparece: ele se recolhe a UMA linha (ver `GravadorDeReuniao`). */
  const horaDeGravar = ["aplicar_reuniao", "extrair_reuniao"].includes(caso?.pauta?.passos?.[0]?.acao);

  /* `?painel=<secao>` leva a coluna até aquela seção da ficha — `copiloto`,
     `agora`, `ficha`, `pendencias`, `reunioes`. `scrollIntoView` e não um estado
     de "seção aberta": a seção está sempre lá, o que muda é onde a coluna está
     olhando. `0` e `1` não são seção (fecham e abrem), e nome que não casa com
     nada deixa a coluna no alto, que é onde ela já abre — endereço errado não
     pode virar tela vazia. */
  useEffect(() => {
    if (!secaoDoPainel || secaoDoPainel === "0" || secaoDoPainel === "1" || reuniao) return;
    const alvo = rolagem.current?.querySelector(`[data-secao='${CSS.escape(secaoDoPainel)}']`);
    alvo?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [secaoDoPainel, reuniao, caso?.registro?.id]);

  /* Trocar de caso volta a coluna para o alto: sem isso, abrir o próximo
     atendimento mostrava a ficha dele começando na altura da anterior. */
  useEffect(() => {
    rolagem.current?.scrollTo({ top: 0 });
  }, [caso?.registro?.id]);

  return (
    <div
      ref={rolagem}
      onScroll={(e) => setRolou(e.currentTarget.scrollTop > 4)}
      /*
       * FLUTUANDO, ela é uma gaveta — e gaveta tem camada de gaveta.
       *
       * Era `z-[14]`, um número solto abaixo de quase tudo: a coluna passava por
       * baixo de superfície da conversa em vez de cobri-la, que é o defeito de
       * "abrir sobre a mesa viva" que o visor já custou. A escala da casa está no
       * CLAUDE.md — caixa (Dialog, Sheet, Drawer) em 50, painel de portal em 120,
       * dica em 130 —, e esta é uma caixa: `z-50`.
       *
       * EMPURRANDO ela não é gaveta, é coluna da mesa: fica no fluxo, sem z
       * nenhum para disputar.
       */
      className={`flex-none overflow-y-auto border-l border-l-border bg-secondary animate-[vSlide_var(--t-media)_var(--e-entra)_both] ${
        empurra
          ? `static ${largo ? "w-[330px]" : "w-[300px]"}`
          : `absolute inset-y-0 right-0 z-50 shadow-[-18px_0_50px_color-mix(in_oklab,_var(--color-foreground)_28%,_transparent)] ${celular ? "w-full" : "w-[340px]"}`
      }`}
    >
      {atual ? (
        <CabecalhoDoPainel
          caso={atual}
          rolou={rolou}
          reuniao={reuniao}
          /*
           * Duas saídas para duas situações, e o mesmo botão.
           *
           * FLUTUANDO ela cobre a conversa, e fechar é `?painel=0` — a gaveta
           * fecha. EMPURRANDO ela é parte da tela, e o ✕ RECOLHE a coluna: a
           * largura volta para a conversa e a alça de 14px na borda a devolve.
           * Sem o segundo, em desktop com zoom alto a ficha de 330px ficava
           * cravada ao lado de uma conversa de 250 e não havia como sair.
           */
          fechar={
            empurra ? (
              aoRecolher ? (
                <Fechar aoTocar={aoRecolher} titulo="Recolher a ficha — a alça na borda a devolve" lado={36} />
              ) : null
            ) : (
              <Fechar aoTocar={() => ajustar({ painel: "0" })} titulo="Fechar o painel" lado={36} />
            )
          }
        />
      ) : null}

      {/*
       * O gravador no ALTO só quando é a hora dele.
       *
       * Gravando, ele fica aqui sempre: o cartão gruda embaixo do cabeçalho e
       * continua à vista em qualquer altura da rolagem, e continua gravando
       * quando alguém abre uma reunião antiga para consultar. Era o defeito da
       * aba — o microfone ligado atrás dela.
       *
       * PARADO, ele se RECOLHE a uma linha quando o passo do servidor não é de
       * reunião. Antes vinha inteiro sempre, e num caso parado em "Aguardando
       * documentos" era o segundo bloco da coluna, acima do que havia para
       * fazer: 130px de gravador para quem estava cobrando um holerite.
       *
       * Recolhe em vez de sair, e a instância continua ÚNICA e sempre montada:
       * duas instâncias seriam dois gravadores, e desmontar esta perderia a
       * gravação em curso — que é o defeito que a aba tinha.
       */}
      {atual ? <GravadorDeReuniao aberto={horaDeGravar} /> : null}

      {reuniao ? <ReuniaoAberta id={reuniao} /> : <FichaDoCaso />}
    </div>
  );
}
